import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useThemePalette } from "common/hooks/theme_palette";
import EventTypeServicesPanel from "./services";

const MetaItem = ({ label, value }) => (
  <Box>
    <Typography fontSize="12px" color="#8A8A8A" mb={0.5}>
      {label}
    </Typography>
    <Typography fontSize="15px" fontWeight="500" color="#2D2D2D">
      {value ?? "-"}
    </Typography>
  </Box>
);

const EventTypeDetails = ({ details, canUpdate }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { oliveGreen } = useThemePalette();
  const nameEn =
    details?.localized_data?.en?.name ||
    details?.localized_data?.name_en ||
    details?.name_en ||
    details?.name ||
    "";
  const nameAr =
    details?.localized_data?.ar?.name ||
    details?.localized_data?.name_ar ||
    details?.name_ar ||
    "";
  const servicesCount = details?.services?.length || 0;

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={2}
        mb={2.5}
      >
        <Box>
          <Typography fontSize="22px" fontWeight="600" mb={0.5}>
            {t("events_event_types.details")}
          </Typography>
          <Typography fontSize="14px" color="#797979">
            #{details?.id} · {servicesCount}{" "}
            {t("events_event_types.services_count")}
          </Typography>
        </Box>
        {canUpdate && (
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            onClick={() => navigate(`?type=form&id=${details?.id}`)}
            sx={{
              borderColor: oliveGreen,
              color: oliveGreen,
              textTransform: "none",
              "&:hover": {
                borderColor: oliveGreen,
                backgroundColor: "rgba(0,0,0,0.02)",
              },
            }}
          >
            {t("common.edit")}
          </Button>
        )}
      </Stack>

      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          border: "1px solid #E8E8E8",
          borderRadius: "10px",
          mb: 2.5,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          gap={2.5}
          alignItems={{ xs: "flex-start", md: "center" }}
        >
          <Box
            sx={{
              width: 140,
              height: 100,
              borderRadius: "8px",
              overflow: "hidden",
              backgroundColor: "#F5F5F5",
              border: "1px solid #EEEEEE",
              flexShrink: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {details?.image ? (
              <img
                src={details.image}
                alt={nameEn}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            ) : (
              <Typography fontSize="12px" color="#A0A0A0">
                {t("chat.no_image")}
              </Typography>
            )}
          </Box>

          <Stack gap={2} flex={1} width="100%">
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
              flexWrap="wrap"
            >
              <Box>
                <Typography fontSize="18px" fontWeight="600">
                  {nameEn}
                </Typography>
                <Typography fontSize="15px" color="#5A5A5A" dir="rtl">
                  {nameAr}
                </Typography>
              </Box>
              <Chip
                size="small"
                label={
                  details?.is_active
                    ? t("events_event_types.is_active")
                    : t("common.no")
                }
                sx={{
                  fontWeight: 600,
                  backgroundColor: details?.is_active
                    ? "rgba(76, 175, 80, 0.12)"
                    : "rgba(158, 158, 158, 0.16)",
                  color: details?.is_active ? "#2E7D32" : "#616161",
                }}
              />
            </Stack>

            <Divider />

            <Stack
              direction={{ xs: "column", sm: "row" }}
              gap={{ xs: 1.5, sm: 4 }}
            >
              <MetaItem
                label={t("events_event_types.name_en")}
                value={nameEn}
              />
              <MetaItem
                label={t("events_event_types.name_ar")}
                value={nameAr}
              />
              <MetaItem
                label={t("events_event_types.display_priority")}
                value={details?.display_priority ?? "-"}
              />
            </Stack>
          </Stack>
        </Stack>
      </Paper>

      {details?.id && (
        <EventTypeServicesPanel
          eventTypeId={details.id}
          canUpdate={canUpdate}
          services={details?.services}
        />
      )}
    </Box>
  );
};

export default EventTypeDetails;
